import React from 'react'
import PropTypes from 'prop-types'
import { Popper as MuiPopper, ClickAwayListener } from '@material-ui/core'
import classNames from 'classnames'

import { Card } from '@components/layout/Card'

import './popper.scss'

/**
 * Example of usage:
 *
 * ```
 * const popper = usePopper({ uniqueId: index })
 *
 * return (
 *   ...
 *   <MyTrigger
 *     aria-describedby={popper.id}
 *     onClick={popper.togglePopper}>
 *     Open menu
 *   </MyTrigger>
 *
 *   <Popper
 *     id={popper.id}
 *     isOpen={popper.isOpen}
 *     anchorElement={popper.anchorElement}
 *     closePopper={popper.closePopper}
 *     variant='card'>
 *     <MyContent />
 *   </Popper>
 * )
 * ```
 */
export const usePopper = ({ uniqueId }) => {
  const [anchorElement, setAnchorElement] = React.useState(null)
  const openPopper = (event) => {
    setAnchorElement(event.currentTarget)
  }
  const closePopper = (event) => {
    event && event.preventDefault()
    setAnchorElement(null)
  }
  const togglePopper = (event) => {
    setAnchorElement(anchorElement ? null : event.currentTarget)
  }
  const isOpen = Boolean(anchorElement)
  const id = isOpen ? `wb-popover-${uniqueId}` : undefined

  return {
    /* Unique ID for use in aria-described-by attribute on the trigger */
    id,
    /* Ref to be passed to Popper */
    anchorElement,
    /* Visibility flag */
    isOpen,
    /* Control handlers */
    openPopper,
    closePopper,
    togglePopper
  }
}

export const Popper = ({
  id, isOpen, closePopper, anchorElement, variant, className, children, containerClassName
}) => {
  const classes = classNames('wb-popper', className)
  let popperContent
  switch (variant) {
    case 'card':
      const cardClasses = classNames('wb-popper__variant--card', containerClassName)
      popperContent = (
        <Card className={cardClasses}>
          {children}
        </Card>
      )
      break
    default:
      popperContent = children
  }

  return (
    <MuiPopper
      className={classes}
      id={id}
      open={isOpen}
      anchorEl={anchorElement}>
      <ClickAwayListener onClickAway={closePopper}>
        {popperContent}
      </ClickAwayListener>
    </MuiPopper>
  )
}


Popper.propTypes = {
  id: PropTypes.string,
  variant: PropTypes.oneOf(['card', 'plain']).isRequired,
  isOpen: PropTypes.bool.isRequired,
  closePopper: PropTypes.func.isRequired,
  anchorElement: PropTypes.element,

  /** CSS class attached to the popper itself */
  className: PropTypes.string,
  /** CSS class attached to the popper's content container. When using variant
  * of 'plain', `className` should be used instead. */
  containerClassName: PropTypes.string
}

Popper.defaultProps = {
  variant: 'card'
}
